import { useWindowSize } from 'react-use'
import { useUser } from './adapters/user'
import Banner from './Banner'
import FileSelect from './components/FileSelect'
import Logo from './components/Logo'
import LogoPro from './components/LogoPro'
import Menu from './components/Menu'

const EXAMPLES = ['bag', 'table', 'paris', 'jacket', 'shoe']

interface HomepageProps {
  setOriginalFile: (f: File) => void
  onFileChange: (f: File) => void
  startWithDemoImage: (img: string) => void
  setShowUpgrade: (showUpgrade: boolean) => void
  setShowSignin: (showSignin: boolean) => void
}

export default function Homepage({
  setOriginalFile,
  onFileChange,
  startWithDemoImage,
  setShowUpgrade,
  setShowSignin,
}: HomepageProps) {
  const windowSize = useWindowSize()
  const user = useUser()

  return (
    <>
      <div className="fixed w-full z-10">
        <Banner />
        <header
          className={[
            'w-full bg-white bg-opacity-70',
            // 'border-b-2 border-black',
            'filter backdrop-blur-3xl z-10 flex px-5 pt-3 pb-3',
            'justify-between items-center sm:items-start',
          ].join(' ')}
        >
          <div className="flex items-center">
            <a href="/#" aria-label="Cleanup Logo">
              {user?.isPro() ? (
                <LogoPro className="w-60 h-14" />
              ) : (
                <Logo className="w-60 h-14" />
              )}
            </a>
            <a
              className="hidden sm:inline-block opacity-50 hover:opacity-100 hover:underline pt-1"
              href="https://clipdrop.co"
              target="_blank"
              rel="noreferrer"
            >
              by Clipdrop
            </a>
          </div>
          <div className="flex items-center space-x-8">
            <a
              className="hidden sm:inline-block hover:underline bg-primary hover:bg-opacity-50 py-2 px-4 rounded-full"
              href="https://clipdrop.co/tools"
              target="_blank"
              rel="noreferrer"
            >
              Other tools
            </a>
            <a
              className="hidden sm:inline-block hover:underline"
              href="#usecases"
            >
              Use cases
            </a>
            <a
              className="hidden sm:inline-block hover:underline"
              href="#pricing"
            >
              Pricing
            </a>
            <a className="hidden sm:inline-block hover:underline" href="#faq">
              FAQ
            </a>
            <a className="hidden sm:inline-block hover:underline" href="#api">
              API
            </a>
            <Menu
              onUpgrade={() => setShowUpgrade(true)}
              onSignin={() => setShowSignin(true)}
            />
          </div>
        </header>
      </div>

      <main
        className={[
          'flex flex-1 flex-col sm:items-center sm:justify-center overflow-hidden',
          // file ? 'items-center justify-center' : '', // center on mobile
          'sm:mt-10',
          'pt-24 sm:pt-40',
          'items-center justify-center',
          'pb-10',
        ].join(' ')}
      >
        <div
          className={[
            'flex flex-col sm:flex-row items-center',
            'space-y-5 sm:space-y-0 sm:space-x-6 p-5 pt-0 pb-10',
          ].join(' ')}
        >
          <div className="sm:max-w-lg lg:max-w-2xl flex flex-col items-center sm:items-start p-0 m-0 space-y-5">
            <h1 className="text-center font-bold sm:text-left text-xl sm:text-3xl lg:text-5xl">
              Remove any unwanted{' '}
              <span className="bg-primary font-varent rounded-l-full rounded-r-full px-2">
                object
              </span>
              ,{' '}
              <span className="bg-primary font-varent rounded-l-full rounded-r-full px-2">
                defect
              </span>
              ,{' '}
              <span className="bg-primary font-varent rounded-l-full rounded-r-full px-2">
                people
              </span>{' '}
              or{' '}
              <span className="bg-primary font-varent rounded-l-full rounded-r-full px-2">
                text
              </span>{' '}
              from your pictures{' '}
              <span className="underline font-varent">in seconds</span>
            </h1>
            {/* <span className="text-gray-500">
        Stunning quality for free on images up to 1024px
      </span> */}
          </div>

          <div className="w-60 sm:w-80 flex items-center rounded-xl overflow-hidden">
            <video
              className="w-60 h-48 sm:w-80 sm:h-64 bg-gray-100 rounded-xl overflow-hidden"
              style={{ transform: 'scale(1.03, 1.03)' }}
              autoPlay
              muted
              loop
              playsInline
            >
              <source
                src="https://storage.googleapis.com/cleanup-pictures.appspot.com/demo_small.mp4"
                type="video/mp4"
              />
              <track kind="captions" />
            </video>
          </div>
        </div>

        <div
          className="h-20 sm:h-52 px-4 w-full my-8 sm:my-0"
          style={{ maxWidth: '800px' }}
        >
          <FileSelect
            onSelection={async f => {
              setOriginalFile(f)
              onFileChange(f)
            }}
          />
        </div>

        <div
          className={[
            'flex flex-col items-center justify-center cursor-pointer',
            'pt-4 sm:pt-10',
          ].join(' ')}
        >
          <span className="mb-4">↓ Try with an example</span>
          <div className="flex space-x-2 sm:space-x-4 px-4">
            {EXAMPLES.slice(0, windowSize.width > 650 ? undefined : 3).map(
              image => (
                <div
                  key={image}
                  onClick={() => startWithDemoImage(image)}
                  role="button"
                  onKeyDown={() => startWithDemoImage(image)}
                  tabIndex={-1}
                >
                  <img
                    className="rounded-md hover:opacity-75 w-24 h-24 object-cover"
                    width="96"
                    height="96"
                    src={`exemples/${image}.thumb.jpeg`}
                    alt={image}
                  />
                </div>
              )
            )}
          </div>
        </div>
      </main>
    </>
  )
}
